import { Outlet, NavLink } from "react-router-dom";
import { Home, Users, Handshake, FileText, Calendar, FolderOpen, MessageSquare, BarChart3, UserCircle } from "lucide-react";
import { NotificationBell } from "@/components/notifications";
import { RoleSwitcher } from "@/components/layout/RoleSwitcher";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { LanguageSelector } from "@/components/ui/language-selector";
import { SignOutButton } from "@/components/layout/SignOutButton";

const getNavigation = (language: string) => [
  { 
    name: language === 'fr' ? "Tableau de bord" : "Dashboard", 
    href: "/dashboard/consultant", 
    icon: Home 
  },
  { 
    name: language === 'fr' ? "Mes Clients" : "My Clients", 
    href: "/dashboard/consultant/clients", 
    icon: Users 
  },
  { 
    name: language === 'fr' ? "Partenariats" : "Partnerships", 
    href: "/dashboard/consultant/partnerships", 
    icon: Handshake 
  },
  { 
    name: language === 'fr' ? "Mandats" : "Mandates", 
    href: "/dashboard/consultant/mandates", 
    icon: FileText 
  },
  { 
    name: language === 'fr' ? "Calendrier" : "Calendar", 
    href: "/dashboard/consultant/calendar", 
    icon: Calendar 
  },
  { 
    name: language === 'fr' ? "Documents" : "Documents", 
    href: "/dashboard/consultant/documents", 
    icon: FolderOpen 
  },
  { 
    name: language === 'fr' ? "Messages" : "Messages", 
    href: "/dashboard/consultant/messages", 
    icon: MessageSquare 
  },
  { 
    name: language === 'fr' ? "Analytiques" : "Analytics", 
    href: "/dashboard/consultant/analytics", 
    icon: BarChart3 
  },
];

export default function ConsultantLayout() {
  const { language } = useLanguage();
  const navigation = getNavigation(language);

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="w-64 border-r border-border bg-card flex flex-col">
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-border">
          <img src="/noBgColor.png" alt="AcquizGroup" className="h-8" />
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              end={item.href === "/dashboard/consultant"}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              <span>{item.name}</span>
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="h-16 border-b border-border bg-card px-6 flex items-center justify-between">
          <h1 className="text-xl font-semibold text-foreground">
            {language === 'fr' ? "Portail Consultant" : "Consultant Portal"}
          </h1>
          <div className="flex items-center gap-3">
            <NavLink to="/consultant/profile">
              <Button variant="ghost" size="sm" className="gap-2">
                <UserCircle className="h-4 w-4" />
                {language === 'fr' ? 'Profil' : 'Profile'}
              </Button>
            </NavLink>
            <RoleSwitcher />
            <LanguageSelector />
            <NotificationBell />
            <SignOutButton />
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
